import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
// import { MatDialogRef, MatSnackBar, MAT_DIALOG_DATA } from '@angular/material';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MatOption } from '@angular/material/core';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiServiceService } from '../api-service.service';
import { CommonPaginationComponent } from '../common-component-pagination/common-component-pagination.component';
import { CommonSearchComponent } from '../common-component-search/common-component-search.component';
import { SharedValidators } from '../shared/validators/shared-validators';
import { CommonInfoTooltipComponent } from '../common-component-info-tooltip/common-component-info-tooltip.component';
import { environment } from 'src/environments/environment';

// Pages/permissions that only exist in the TCU4G build (AIS140 ticketing + related
// reports). Hidden from Role Management's permission list on any other project build
// (e.g. TCU2G) since granting them there would do nothing - matches the
// `projectName === 'TCU4G'` gates already on these features' own nav items/buttons.
const TCU4G_ONLY_PAGES = [
  'Ticket TAT Time Report',
  'Dealer FOTA',
  'My AIS 140 Tickets',
  'My AIS 140 Tickets Master Access',
  'Certification Automation',
  'Server Monitoring Dashboard',
  'Vahan Tagging Automation',
  'My Ticket Details page',
  'Dealer Master',
  'Institutional Sales',
  'AIS140 Ticket TML Request Logs',
  'AIS140 Ticket AEPL Response Logs',
  'AIS140 Ticket Status Update Logs',
];

// What each checkbox actually turns on, per page - written for someone new to the
// codebase who has no way to know this just by reading the checkbox labels. Only
// pages/flags with a real, wired effect are listed here; a flag with no key for a
// given page has no effect anywhere in the app, so the row's info icon is omitted
// entirely (see getActionInfo below) rather than showing a "no impact" placeholder.
const PERMISSION_ACTION_INFO: Record<
  string,
  Partial<Record<'view' | 'create' | 'update' | 'delete', string>>
> = {
  'Device Dashboard': {
    view: 'Lets the user open the Device Dashboard page (KPI cards, graphs, device lists).',
  },
  'Device Details': {
    view: "Lets the user open a device's full details page.",
  },
  'Ticket Dashboard': {
    view: 'Lets the user open the Ticket Dashboard (KPI cards, graphs, ticket list).',
  },
  'Ticket TAT Time Report': {
    view: 'Shows the TAT report download button on the Ticket Dashboard.',
  },
  'My AIS 140 Tickets': {
    view: 'Lets the user open the My AIS 140 Tickets list.',
  },
  'My AIS 140 Tickets Master Access': {
    view: '• Shows ALL tickets in the list, not just tickets assigned to the user\n• Shows the Individual Performance graph on the Ticket Dashboard',
    update:
      '• Lets the user reassign tickets to other users/states\n• Lets the user edit closed/cancelled tickets\n• Unlocks the Ticket Assignment Master page',
  },
  'Certification Automation': {
    view: 'Shows the Certification Automation shortcut card in Other Links (opens an external tool, not an in-app page).',
  },
  'Server Monitoring Dashboard': {
    view: 'Shows the Server Monitoring Dashboard shortcut card in Other Links (opens an external tool, not an in-app page).',
  },
  'Vahan Tagging Automation': {
    view: 'Shows the Vahan Tagging Automation shortcut card in Other Links (opens an external tool, not an in-app page).',
  },
  'My Ticket Details page': {
    view: "Lets the user open an individual ticket's details page.",
    update:
      '• Lets the user edit tickets from the My AIS 140 Tickets list\n• Lets the user upload the certificate\n• Lets the user update the ticket stage/status',
  },
  'Dealer Master': {
    view: 'Lets the user open the Dealer Master list.',
    create: 'Shows the Add Dealer button.',
    update: 'Shows the Edit button on a dealer row.',
    delete: 'Shows the Delete button on a dealer row.',
  },
  'Institutional Sales': {
    view: 'Lets the user open the Institutional Sales page and upload new records.',
  },
  'Government Servers': {
    view: "Lets the user open the Government Servers list and a server's details page.",
    create: 'Shows the Add Server button and lets saving a new server.',
    update: 'Shows the Edit button and lets saving changes to a server.',
    delete: 'Shows the Delete button on a server row.',
  },
  'FOTA Batch': {
    view: "Lets the user open the FOTA Batch list and a batch's device/status details.",
    create: 'Shows the Add Batch button, and lets adding devices to a batch.',
    update:
      "Lets the user open a batch's details page and shows 'Start FOTA' option.",
    delete: 'Shows the Abort button for a batch and for devices within it.',
  },
  'Manual FOTA': {
    view: 'Lets the user open the Manual FOTA page (also linked from FOTA Batch and Device Details as a shortcut).',
    create: 'Lets the user submit a new manual FOTA request.',
    delete: 'Lets the user abort a device within a manual FOTA request.',
  },
  'OTA Batch': {
    view: "Lets the user open the OTA Batch list and a batch's device/status details.",
    create: 'Shows the Add Batch button and lets creating a new batch.',
    delete: 'Shows the Abort button for a batch (list and its details page).',
  },
  'OTA Master': {
    view: 'Lets the user open the OTA Master list.',
    create: 'Shows the Add button on OTA Master.',
    update: 'Shows the Edit button on an OTA Master row.',
    delete: 'Shows the Delete button on an OTA Master row.',
  },
  'Manual OTA': {
    view: 'Lets the user open the Manual OTA page (also linked from OTA Batch and Device Details as a shortcut).',
    create: 'Lets the user submit a new manual OTA request.',
    delete: 'Lets the user abort a device within a manual OTA request.',
  },
  'Device Model': {
    view: 'Lets the user open the Device Model list.',
    create:
      "Shows the Add Model button (list), and lets adding new firmware to a model (details page).",
    update:
      "Shows the link to open a model's details/firmware page (list), and lets editing existing firmware there (details page).",
    delete: 'Shows the Delete button on a model row (list).',
  },
  'Dealer FOTA': {
    view: 'Lets the user open the Dealer FOTA page and its Approved Files sub-page.',
    create: 'Shows the Add button on the Dealer FOTA page.',
  },
  'Dispatch Device': {
    view: 'Lets the user open the Dispatch Device list.',
    create: 'Shows the Add Device button and its Add page.',
    update: 'Shows the Edit button on a dispatch device row.',
    delete: 'Shows the Delete button on a dispatch device row.',
  },
  'AIS140 Ticket TML Request Logs': {
    view: 'Lets the user open the AIS140 TML Request Logs page.',
  },
  'AIS140 Ticket AEPL Response Logs': {
    view: 'Lets the user open the AIS140 AEPL Response Logs page.',
  },
  'AIS140 Ticket Status Update Logs': {
    view: 'Lets the user open the AIS140 Status Update Logs page.',
  },
  'Device System Activity Log': {
    view: 'Lets the user open the Device System Activity Log page.',
  },
  'Device State Config': {
    view: 'Lets the user open the Device State Config page.',
  },
  'Device Vin Config': {
    view: 'Lets the user open the Device Vin Config page.',
  },
  'SIM Data Details': {
    view: 'Lets the user open the SIM Data Details page.',
  },
  'User Management': {
    view: 'Lets the user open the User Management list.',
    create: 'Shows the Add User button.',
    update: 'Shows the Edit button on a user row.',
    delete: 'Shows the Delete button on a user row.',
  },
  'User Profile': {
    view: 'Lets the user open their own Profile page.',
    update: 'Lets the user edit their own profile details.',
  },
};

@Component({
  selector: 'app-role-management',
  templateUrl: './role-management.component.html',
  styleUrls: ['./role-management.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    NgxPaginationModule,
    MatError,
    MatSelect,
    NgxMatSelectSearchModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatOption,
    DatePipe,
    MatTooltip,
    CommonPaginationComponent,
    CommonSearchComponent,
    CommonInfoTooltipComponent,
  ],
})
export class RoleManagementComponent implements OnInit {
  page = 1;
  size = 10;
  totalLength = 0;
  search = '';
  listOfRole = [];

  submitButton = true;
  updateButton = false;
  rid = '';

  addRoleForm: FormGroup = this.formBuilder.group({});
  roleListSearch: FormGroup = this.formBuilder.group({});

  roleTypeList = [
    { name: 'Super Administrator', value: 'SA', hierarchyLevel: 0 },
    { name: 'Administrator', value: 'AD', hierarchyLevel: 1 },
    { name: 'Manager', value: 'MN', hierarchyLevel: 2 },
    { name: 'Team Lead', value: 'TL', hierarchyLevel: 3 },
    { name: 'User', value: 'US', hierarchyLevel: 4 },
    { name: 'External', value: 'EX', hierarchyLevel: 5 },
  ];
  roleGroupList: { groupName: string; _id: string }[] = [];
  viewRoleGroup = false;

  userRoleFormView = false;

  userPermission = [];
  roleType = '';

  operationType = '';
  selectAll = false;
  viewAll = false;
  createAll = false;
  updateAll = false;
  deleteAll = false;
  allPages = [];

  permission = [];
  view = false;
  dashboard_view = '';

  isAddRole = false;

  constructor(
    private formBuilder: FormBuilder,
    public apiMicroservice: ApiServiceService,
    private spinner: NgxSpinnerService,
    public router: Router
  ) {}

  ngOnInit() {
    this.checkPermission();
    this.setUserRoleForm();
    if (this.userRoleFormView) {
      this.fetchRoleList();
      this.fetchRoleGroupList();
    }
  }

  checkPermission() {
    this.userPermission = JSON.parse(
      this.apiMicroservice.decryptData(
        sessionStorage.getItem('userPermission') || '[]'
      )
    );
    this.roleType = this.apiMicroservice.decryptData(
      sessionStorage.getItem('roleType') || ''
    );
    if (this.roleType === 'SA') {
      this.userRoleFormView = true;
    } else {
      this.navigateBack();
    }
  }

  setUserRoleForm() {
    this.addRoleForm = this.formBuilder.group({
      userRole: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.nonNumericNoSpecialChars,
      ]),
      roleType: new FormControl('', [Validators.required]),
      hierarchyLevel: new FormControl('', [Validators.required]),
      groupId: new FormControl('', []),
      groupName: new FormControl('', []),
      createdAt: new Date(),
    });

    this.roleListSearch = this.formBuilder.group({
      searchRole: new FormControl(''),
    });
  }

  fetchRoleList() {
    this.spinner.show();
    this.apiMicroservice
      .getRole(this.page, this.size, this.search, '', '')
      .subscribe((res) => {
        this.listOfRole = res.data;
        this.totalLength = res.totalItems;
        this.apiMicroservice.openSnackbar('Fetched Successfully');
        this.spinner.hide();
      });
  }

  onPageChange(event: number) {
    this.page = event;
    this.fetchRoleList();
  }

  onSizeChange(event: number) {
    this.page = 1;
    this.size = event;
    this.fetchRoleList();
  }

  onSearch(event: string) {
    this.page = 1;
    this.search = event;
    this.roleListSearch
      ?.get('searchRole')
      ?.setValue(this.search ? this.search : '');
    this.fetchRoleList();
  }

  deleteRole(userRole) {
    if (confirm('Are you sure?')) {
      this.spinner.show();
      this.apiMicroservice.deleteRole(userRole).subscribe({
        next: (res) => {
          if (res.status) {
            this.apiMicroservice.openSnackbar(res.message);
            this.spinner.hide();
            this.ngOnInit();
          } else {
            this.spinner.hide();
            this.apiMicroservice.openSnackbar('Operation Failed');
          }
        },
        error: (ERROR: HttpErrorResponse) => {
          this.spinner.hide();
          this.apiMicroservice.openSnackbar(ERROR.error.message);
        },
      });
    }
  }

  prePopulation(id) {
    this.apiMicroservice.getRoleById(id).subscribe({
      next: (res) => {
        if (res.status) {
          const data = res.data[0];
          this.updateButton = true;
          this.submitButton = false;
          this.addRoleForm = this.formBuilder.group({
            userRole: new FormControl(data.userRole, [
              SharedValidators.requiredAndNoEmptySpaces,
              SharedValidators.nonNumericNoSpecialChars,
            ]),
            roleType: new FormControl(data.roleType, [Validators.required]),
            hierarchyLevel: new FormControl(data.hierarchyLevel, [
              Validators.required,
            ]),
            groupId: new FormControl(data.groupId?._id, []),
            groupName: new FormControl(data.groupName, []),
            updatedAt: new Date(),
          });
          if (this.addRoleForm.controls['groupId'].value) {
            this.viewRoleGroup = true;
          }
          this.rid = data._id;
          this.addRole('update', data.userPermission);
        } else {
          this.apiMicroservice.openSnackbar('Operation Failed');
        }
      },
      error: (ERROR: HttpErrorResponse) => {
        this.spinner.hide();
        this.apiMicroservice.openSnackbar(ERROR.error.message);
      },
    });
  }

  toggleIsAddRole() {
    this.isAddRole = !this.isAddRole;
  }

  fetchRoleGroupList() {
    this.apiMicroservice.getAllRoleGroup().subscribe((res) => {
      this.roleGroupList = res.data;
    });
  }

  onRoleTypeChange(event: MatSelectChange) {
    const selectedRole = this.roleTypeList.find(
      (role) => role.value === event.value
    );
    this.addRoleForm.controls['roleType'].setValue(selectedRole?.value);
    this.addRoleForm.controls['hierarchyLevel'].setValue(
      selectedRole?.hierarchyLevel
    );
    if (
      selectedRole?.value === 'MN' ||
      selectedRole?.value === 'TL' ||
      selectedRole?.value === 'US' ||
      selectedRole?.value === 'EX'
    ) {
      this.viewRoleGroup = true;
      this.addRoleForm.controls['groupId'].setValue('');
      this.addRoleForm.controls['groupId'].setValidators(Validators.required);
      this.addRoleForm.controls['groupId'].updateValueAndValidity();
      this.addRoleForm.controls['groupName'].setValue('');
      this.addRoleForm.controls['groupName'].setValidators(Validators.required);
      this.addRoleForm.controls['groupName'].updateValueAndValidity();
    } else if (selectedRole?.value === 'AD' || selectedRole?.value === 'SA') {
      this.onSelectAllPagesChange(true);
      this.viewRoleGroup = false;
      this.addRoleForm.controls['groupId'].setValue('');
      this.addRoleForm.controls['groupId'].removeValidators(
        Validators.required
      );
      this.addRoleForm.controls['groupId'].updateValueAndValidity();
      this.addRoleForm.controls['groupName'].setValue('');
      this.addRoleForm.controls['groupName'].removeValidators(
        Validators.required
      );
      this.addRoleForm.controls['groupName'].updateValueAndValidity();
    }
  }

  onRoleGroupChange(event: MatSelectChange) {
    const selectedGroup = this.roleGroupList.find(
      (role) => role?._id === event.value
    );
    this.addRoleForm.controls['groupId'].setValue(selectedGroup?._id);
    this.addRoleForm.controls['groupName'].setValue(selectedGroup?.groupName);
  }

  saveRole() {
    if (this.addRoleForm.valid) {
      this.spinner.show();
      const payload = this.addRoleForm.value;
      payload.userPermission = this.allPages;
      this.apiMicroservice.addRole(payload).subscribe({
        next: (res) => {
          this.viewRoleGroup = false;
          if (res.status) {
            this.spinner.hide();
            this.apiMicroservice.openSnackbar(res.message);
            this.isAddRole = !this.isAddRole;
            this.ngOnInit();
          } else {
            this.spinner.hide();
          }
        },
        error: (ERROR: HttpErrorResponse) => {
          this.spinner.hide();
          const example = ERROR.error.message;
          this.apiMicroservice.openSnackbar(example);
        },
      });
    } else {
      SharedValidators.markAllAsTouched(this.addRoleForm);
    }
  }

  updateRole() {
    if (this.addRoleForm.valid) {
      this.spinner.show();
      const payload = this.addRoleForm.value;
      payload.id = this.rid;
      payload.userPermission = this.allPages;
      this.apiMicroservice.updateRole(payload).subscribe({
        next: (res) => {
          this.viewRoleGroup = false;
          if (res.status) {
            this.updateButton = false;
            this.submitButton = true;
            this.apiMicroservice.openSnackbar(res.message);
            this.spinner.hide();
            this.isAddRole = !this.isAddRole;
            this.ngOnInit();
          } else {
            this.spinner.hide();
            this.apiMicroservice.openSnackbar('Operation Failed');
          }
        },
        error: (ERROR: HttpErrorResponse) => {
          this.spinner.hide();
          this.apiMicroservice.openSnackbar(ERROR.error.message);
        },
      });
    } else {
      SharedValidators.markAllAsTouched(this.addRoleForm);
    }
  }

  addRole(operationType, data) {
    this.operationType = operationType;
    this.menuPermissionList();
    if (operationType === 'add') {
      this.isAddRole = true;
      this.addRoleForm.reset();
      this.submitButton = true;
      this.updateButton = false;
      this.selectAll = false;
      this.viewAll = false;
      this.createAll = false;
      this.updateAll = false;
      this.deleteAll = false;
      this.viewRoleGroup = false;
      this.onSelectAllPagesChange(false);
    } else if (operationType === 'update') {
      this.isAddRole = true;
      if (data.length > 0) {
        for (const page of this.allPages) {
          for (const item of data) {
            if (page.pageName === item.pageName) {
              page.view = item.view;
              page.create = item.create;
              page.update = item.update;
              page.delete = item.delete;
              break; // Exit inner loop once a match is found
            }
          }
        }
      }
    }
  }

  onSelectAllPagesChange(e) {
    this.selectAll = e;
    this.viewAll = e;
    this.createAll = e;
    this.deleteAll = e;
    this.updateAll = e;

    if (e === true) {
      for (const page of this.allPages) {
        page.selectAll = true;
        page.view = true;
        page.create = true;
        page.update = true;
        page.delete = true;
      }
    }

    if (e === false) {
      for (const page of this.allPages) {
        page.selectAll = false;
        page.view = false;
        page.create = false;
        page.update = false;
        page.delete = false;
      }
    }
  }

  onSelectViewPagesChange(e) {
    this.viewAll = e;
    if (e === true) {
      for (const page of this.allPages) {
        page.selectAll = false;
        page.view = true;
      }
    }

    if (e === false) {
      for (const page of this.allPages) {
        page.selectAll = false;
        page.view = false;
        page.create = false;
        page.update = false;
        page.delete = false;
      }
    }
  }

  onSelectCreatePagesChange(e) {
    this.createAll = e;
    if (e === true) {
      for (const page of this.allPages) {
        page.selectAll = false;
        page.create = true;
      }
    }

    if (e === false) {
      for (const page of this.allPages) {
        page.selectAll = false;
        page.create = false;
      }
    }
  }

  onSelectUpdatePagesChange(e) {
    this.updateAll = e;
    if (e === true) {
      for (const page of this.allPages) {
        page.selectAll = false;
        page.update = true;
      }
    }

    if (e === false) {
      for (const page of this.allPages) {
        page.selectAll = false;
        page.update = false;
      }
    }
  }

  onSelectDeletePagesChange(e) {
    this.deleteAll = e;
    if (e === true) {
      for (const page of this.allPages) {
        page.selectAll = false;
        page.delete = true;
      }
    }

    if (e === false) {
      for (const page of this.allPages) {
        page.selectAll = false;
        page.delete = false;
      }
    }
  }

  onDeleteChange(e) {
    if (e.target.checked === true) {
      this.permission.push(e.target.value);
      for (const page of this.allPages) {
        if (e.target.value === page.pageName) {
          page.delete = true;
        }
      }
    }

    if (e.target.checked === false) {
      this.permission.splice(this.permission.indexOf(e.target.value), 1);
      for (const page of this.allPages) {
        if (e.target.value === page.pageName) {
          page.delete = false;
        }
      }
    }
  }

  onUpdateChange(e) {
    if (e.target.checked === true) {
      this.permission.push(e.target.value);
      for (const page of this.allPages) {
        if (e.target.value === page.pageName) {
          page.update = true;
        }
      }
    }

    if (e.target.checked === false) {
      this.permission.splice(this.permission.indexOf(e.target.value), 1);
      for (const page of this.allPages) {
        if (e.target.value === page.pageName) {
          page.update = false;
        }
      }
    }
  }

  onCreateChange(e) {
    if (e.target.checked === true) {
      this.permission.push(e.target.value);
      for (const page of this.allPages) {
        if (e.target.value === page.pageName) {
          page.create = true;
        }
      }
    }

    if (e.target.checked === false) {
      this.permission.splice(this.permission.indexOf(e.target.value), 1);
      for (const page of this.allPages) {
        if (e.target.value === page.pageName) {
          page.create = false;
        }
      }
    }
  }

  onViewChange(e) {
    if (e.target.checked === true) {
      this.permission.push(e.target.value);
      for (const page of this.allPages) {
        if (e.target.value === page.pageName) {
          page.view = true;
        }
      }
    }

    if (e.target.checked === false) {
      this.permission.splice(this.permission.indexOf(e.target.value), 1);
      for (const page of this.allPages) {
        if (e.target.value === page.pageName) {
          page.selectAll = false;
          page.view = false;
          page.create = false;
          page.update = false;
          page.delete = false;
        }
      }
    }

    if (e.target.checked && e.target.value === 'dashboard_view') {
      this.view = true;
    } else {
      //  this.tab = true;
      this.view = false;
    }
  }

  onSelectAllChange(e) {
    if (e.target.checked === true) {
      for (const page of this.allPages) {
        if (e.target.value === page.pageName) {
          page.selectAll = true;
          page.view = true;
          page.create = true;
          page.update = true;
          page.delete = true;
        }
      }
    }

    if (e.target.checked === false) {
      for (const page of this.allPages) {
        if (e.target.value === page.pageName) {
          page.selectAll = false;
          page.view = false;
          page.create = false;
          page.update = false;
          page.delete = false;
        }
      }
    }
  }

  menuPermissionList() {
    this.allPages = [];
    const isTCU4G = environment.projectName === 'TCU4G';
    for (let i = 0; i < this.router.config.length; i++) {
      const routePath = this.router.config[i].data;
      if (routePath !== undefined) {
        if (!isTCU4G && TCU4G_ONLY_PAGES.includes(routePath.name)) {
          continue;
        }
        this.allPages.push({
          pageName: routePath.name,
          view: false,
          create: false,
          update: false,
          delete: false,
          isTab: routePath.isTab,
          id: i,
          isUserTab: routePath.isUserTab,
        });
      }
    }
  }

  navigateBack() {
    this.apiMicroservice.back();
  }

  getActionInfo(
    pageName: string,
    action: 'view' | 'create' | 'update' | 'delete'
  ): string {
    return PERMISSION_ACTION_INFO[pageName]?.[action] || '';
  }
}
